// Phase 84.M.45 — Reusable number input cho admin forms.
//
// Mục tiêu:
//   1. Cho phép xoá hoàn toàn (kể cả số 0 đứng đầu) bằng phím Backspace —
//      KHÔNG bị kẹt giá trị "0" như native `<input type="number">`.
//   2. Live-format dấu chấm phân cách hàng nghìn theo locale vi-VN
//      (1000 → "1.000", 1234567 → "1.234.567"). Áp dụng khi `thousand=true`.
//   3. Hỗ trợ thập phân (rating, set scores) qua prop `decimal=true`. Dấu
//      thập phân dùng "." để consistent với phần còn lại của app
//      (user choice 3a — Phase 84.M.45).
//   4. Stripping leading zeros: gõ "00123" auto thành "123". Số "0" đơn
//      lẻ chỉ giữ khi user xoá hết rồi gõ "0".
//
// Props
// -----
//  value       — Số (number) hoặc null/undefined (rỗng).
//  onChange(v) — Trả Number nếu có giá trị, hoặc null nếu trống.
//  thousand    — Bật dấu chấm hàng nghìn (mặc định true).
//  decimal     — Cho phép nhập "." thập phân. Mặc định false (số nguyên).
//  min / max   — Giới hạn (chỉ validate khi blur).
//  placeholder, disabled, className, dataTestid, suffix, prefix, autoFocus, name, onBlur — pass-through.
import { useEffect, useRef, useState } from "react";

function formatThousand(intStr) {
  if (!intStr) return "";
  // Strip leading zeros (giữ lại "0" nếu là số duy nhất).
  const cleaned = intStr.replace(/^0+(?=\d)/, "") || (intStr.startsWith("0") ? "0" : "");
  return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function rawToFormatted(raw, { thousand, decimal }) {
  if (raw === "" || raw === null || raw === undefined) return "";
  const negative = raw.startsWith("-");
  let body = negative ? raw.slice(1) : raw;
  if (decimal) {
    const [intPart, decPart = ""] = body.split(".");
    const intFmt = thousand ? formatThousand(intPart || "0") : (intPart || "0").replace(/^0+(?=\d)/, "") || "0";
    const showDot = body.includes(".");
    body = showDot ? `${intFmt}.${decPart}` : intFmt;
  } else {
    body = thousand ? formatThousand(body) : body.replace(/^0+(?=\d)/, "") || (body === "0" ? "0" : "");
  }
  return negative ? `-${body}` : body;
}

function parseValueToRaw(value, { decimal }) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  if (decimal) return String(num);
  return String(Math.trunc(num));
}

function sanitizeKeystroke(input, { decimal }) {
  // Phase 84.M.54 — iOS Vietnamese keyboard với inputMode="decimal" hiển
  // thị dấu "," (locale-aware) thay vì ".". Tự convert "," → "." để
  // user nhập được số thập phân.
  let s = decimal ? input.replace(/,/g, ".") : input;
  // Strip mọi ký tự không hợp lệ. Cho phép tối đa 1 dấu "-" đầu, và (nếu
  // decimal) tối đa 1 dấu ".".
  s = s.replace(/[^\d.\-]/g, "");
  // Chỉ giữ "-" ở vị trí đầu tiên.
  if (s.includes("-")) {
    const neg = s.startsWith("-") ? "-" : "";
    s = neg + s.replace(/-/g, "");
  }
  if (!decimal) {
    s = s.replace(/\./g, "");
  } else {
    // Chỉ giữ dấu "." đầu tiên.
    const firstDot = s.indexOf(".");
    if (firstDot >= 0) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
  }
  return s;
}

export default function NumberInput({
  value,
  onChange,
  thousand = true,
  decimal = false,
  min,
  max,
  placeholder,
  disabled,
  className = "",
  dataTestid,
  prefix,
  suffix,
  autoFocus,
  name,
  onBlur,
  inputMode,
  ...rest
}) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Local raw string state (no formatting). Re-synced when external value
  // changes AND user is not actively editing this field.
  const [raw, setRaw] = useState(() => parseValueToRaw(value, { decimal }));
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current) return; // Don't clobber user typing.
    setRaw(parseValueToRaw(value, { decimal }));
  }, [value, decimal]);

  const display = rawToFormatted(raw, { thousand, decimal });

  const emit = (nextRaw) => {
    setRaw(nextRaw);
    const cb = onChangeRef.current;
    if (typeof cb !== "function") return;
    if (nextRaw === "" || nextRaw === "-") {
      cb(null);
      return;
    }
    const n = decimal ? parseFloat(nextRaw) : parseInt(nextRaw, 10);
    if (Number.isFinite(n)) cb(n);
    else cb(null);
  };

  const handleChange = (e) => {
    // Phase 84.M.54 — iOS Vietnamese keyboard cho `inputMode="decimal"`
    // emit dấu "," làm decimal separator. Normalize "," → "." sớm để
    // logic bên dưới (vốn coi "." là decimal sep) chạy đúng.
    const rawIn = decimal ? e.target.value.replace(/,/g, ".") : e.target.value;
    // Strip thousand separators + sanitize keystroke characters.
    let next = rawIn.replace(/\./g, "");
    if (decimal && rawIn.includes(".")) {
      // Re-insert single "." at the original position relative to digits.
      const formatted = rawIn;
      const dotInFormatted = formatted.indexOf(".");
      const digitsBeforeDot = formatted.slice(0, dotInFormatted).replace(/\D/g, "").length;
      next = next.slice(0, digitsBeforeDot) + "." + next.slice(digitsBeforeDot);
    }
    next = sanitizeKeystroke(next, { decimal });
    emit(next);
  };

  const handleBlur = (e) => {
    focusedRef.current = false;
    let next = raw;
    // Normalise: empty stays empty; trim trailing decimal point.
    if (next.endsWith(".")) next = next.slice(0, -1);
    if (next === "-" || next === "") {
      setRaw("");
      onChangeRef.current?.(null);
      if (onBlur) onBlur(e);
      return;
    }
    let n = decimal ? parseFloat(next) : parseInt(next, 10);
    if (!Number.isFinite(n)) {
      setRaw("");
      onChangeRef.current?.(null);
      if (onBlur) onBlur(e);
      return;
    }
    if (typeof min === "number" && n < min) n = min;
    if (typeof max === "number" && n > max) n = max;
    const clamped = decimal ? String(n) : String(Math.trunc(n));
    setRaw(clamped);
    onChangeRef.current?.(n);
    if (onBlur) onBlur(e);
  };

  return (
    <div className={`relative ${prefix || suffix ? "flex items-center" : ""}`}>
      {prefix && (
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/50 text-[12px] pointer-events-none">
          {prefix}
        </span>
      )}
      <input
        type="text"
        inputMode={inputMode || (decimal ? "decimal" : "numeric")}
        value={display}
        onChange={handleChange}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        name={name}
        className={`${className} ${prefix ? "pl-8" : ""} ${suffix ? "pr-8" : ""}`.trim()}
        data-testid={dataTestid}
        {...rest}
      />
      {suffix && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/50 text-[12px] pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}
